import * as React from 'react';
import type {
  EmptyT,
  QuestionModuleResult,
} from '@mentimeter/question-modules-types';
import { useQuestionModulesPreviewResults } from '@mentimeter/slides-preview-results';
import { useQuestion } from '../data-mediation/question';
import { useQuestionResults } from '../data-mediation/question-results';
import { defaultState, SfinxResultsProvider } from '../data-mediation/results';
import { getIsQuizSlide } from '../utils/isQuizSlide';
import { useTestVotesState } from './presentation-actions/testVotes';

const EMPTY_RESULTS: EmptyT = {
  respondents: 0,
  type: 'empty',
  results: [],
  isPlaceholderResults: true,
};

const Results = ({ children }: { children: React.ReactNode }) => {
  const { type, choices, range, hide_results, module_id, sub_type } =
    useQuestion();

  const { testVotesShowing } = useTestVotesState();
  const isQuizSlide = getIsQuizSlide(type);
  const previewResultsEnabled = !isQuizSlide && testVotesShowing;

  const currentResult = useQuestionResults();
  const resultsAreMismatched = currentResult?.type !== type;

  const results = (currentResult ?? defaultState) as QuestionModuleResult;

  const previewResults =
    useQuestionModulesPreviewResults({
      moduleId: module_id,
      previewResultsEnabled,
      choices,
      range,
      segments: results.segments,
    }) ?? EMPTY_RESULTS;

  // Some slides have their own hidden state and should receive the results even if the results are hidden
  const isBars = type === 'choices' && sub_type === 'choices';
  const shouldAlwaysReturnResults =
    type === 'wordcloud' || type === 'open' || isBars;

  const questionResults = React.useMemo(() => {
    if (
      previewResultsEnabled &&
      !hide_results &&
      type === previewResults.type
    ) {
      return previewResults;
    }

    if (resultsAreMismatched) {
      // Results can be mismatched for a short period of time when the slide type is changed
      return EMPTY_RESULTS;
    }

    if (shouldAlwaysReturnResults) {
      if (previewResultsEnabled) {
        return previewResults.type === type ? previewResults : EMPTY_RESULTS;
      }
      return results;
    }

    // hide_results stem from proxy property in  packages/compatibility/src/backward-compatibility/question-properties/get-interactive-question-property.ts
    // In short, responseVisibility === RESPONSES_HIDDEN means hide_results = true
    if (hide_results) {
      return EMPTY_RESULTS;
    }

    return results;
  }, [
    hide_results,
    results,
    previewResultsEnabled,
    previewResults,
    type,
    shouldAlwaysReturnResults,
    resultsAreMismatched,
  ]);

  return (
    <SfinxResultsProvider value={questionResults}>
      {children}
    </SfinxResultsProvider>
  );
};

export default Results;
